import { useTranslation } from '@mukil/i18n';
import { GlassCard } from '@jumpstone/ui-react';
import { FieldRow } from '../../../components/common/field_row';
import { EmploymentTypeBadge } from '../employment_type_badge';
import { fte_bps_to_percent } from '../../../lib/worker_types';
import { format_date } from '../../../lib/format';
import type { Employee, EmploymentEvent } from '../../../data/types';
import {
  localise_fixture,
  localise_person_title,
} from '../../../data/mock_labels';

const EVENT_KEY = {
  joined: 'employees.event.joined',
  promoted: 'employees.event.promoted',
  on_leave: 'employees.event.on_leave',
  terminated: 'employees.event.terminated',
} as const;

const LEAVE_REASON_KEY = {
  parental: 'employees.leave_reason.parental',
  medical: 'employees.leave_reason.medical',
} as const;

const EXIT_REASON_KEY = {
  redundancy: 'employees.exit_reason.redundancy',
  resignation: 'employees.exit_reason.resignation',
} as const;

export function JobTab({
  employee,
}: {
  employee: Employee;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');

  return (
    <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
      <GlassCard className="rounded-2xl p-5">
        <h2 className="text-[14.5px] font-bold mb-1.5">
          {t('employees.job_information')}
        </h2>
        <FieldRow label={t('employees.job_title')}>
          {localise_fixture(employee.job_title, locale)}
        </FieldRow>
        <FieldRow label={t('employees.department')}>
          {localise_fixture(employee.department, locale)}
        </FieldRow>
        <FieldRow label={t('employees.reports_to')}>
          {employee.manager === null
            ? t('common.none')
            : localise_person_title(employee.manager, locale)}
        </FieldRow>
        <FieldRow label={t('employees.employment_type')}>
          <EmploymentTypeBadge type={employee.employment_type} />
        </FieldRow>
        {employee.employment_type === 'part_time' && (
          <FieldRow label={t('employees.form_fte_percent')}>
            {t('employees.fte_value', {
              percent: fte_bps_to_percent(employee.fte_bps) ?? 100,
            })}
          </FieldRow>
        )}
        <FieldRow label={t('employees.start_date')}>
          {format_date(employee.start_date, locale)}
        </FieldRow>
        <FieldRow label={t('employees.work_location')}>
          {localise_fixture(employee.work_location, locale)}
        </FieldRow>
      </GlassCard>

      <GlassCard className="rounded-2xl p-5">
        <h2 className="text-[14.5px] font-bold mb-1.5">
          {t('employees.status_history')}
        </h2>
        {employee.status_history.map((event) => (
          <div
            key={`${event.kind}-${event.date}`}
            className="flex gap-2.5 py-2.5 text-[13px] border-b border-[color:var(--line)] last:border-b-0"
          >
            <span className="text-[color:var(--ink-muted)] min-w-[96px]">
              {format_date(event.date, locale)}
            </span>
            <span>{describe_event(event, t, locale)}</span>
          </div>
        ))}
      </GlassCard>
    </div>
  );
}

function describe_event(
  event: EmploymentEvent,
  t: (key: string, params?: Record<string, string | number>) => string,
  locale: string,
): string {
  switch (event.kind) {
    case 'joined':
    case 'promoted':
      return t(EVENT_KEY[event.kind], {
        role: localise_fixture(event.role, locale),
      });
    case 'on_leave':
      return t(EVENT_KEY[event.kind], {
        reason: t(LEAVE_REASON_KEY[event.reason]),
      });
    case 'terminated':
      return t(EVENT_KEY[event.kind], {
        reason: t(EXIT_REASON_KEY[event.reason]),
      });
  }
}
